import React, {
  FunctionComponent, useState, useCallback, useMemo,
} from 'react';
import { InputRange, Pictogram } from '@doveit/lego';
import { formatEuro } from '@doveit/hammer';
import calculateSaving from '../../utils/saving-calculator/savingCalculator';
import * as styles from './SavingCalculator.style';

export const PROPERTY_MIN_VALUE = 50000;
export const PROPERTY_MAX_VALUE = 2500000;
export const PROPERTY_VALUE_UNIT = 10000;

export const MIN_SAVING_WITHOUT_VAT = 4_000;

export const FEE_MIN_VALUE = 2;
export const FEE_MAX_VALUE = 6;
export const FEE_VALUE_UNIT = 0.5;

export interface SavingCalculatorProps {
  initialPropertyValue?: number,
  initialFee?: number,
  compact?: boolean,
  priceLabel?: string,
}

const SavingCalculator: FunctionComponent<SavingCalculatorProps> = ({
  initialPropertyValue = 300000,
  initialFee = 4,
  priceLabel = 'Con Dove.it risparmi',
  compact,
}) => {
  const [propertyValue, setPropertyValue] = useState(initialPropertyValue);
  const [fee, setFee] = useState(initialFee);

  const onPropertyValueChange = useCallback(
    (e) => setPropertyValue(e.target.value), [],
  );

  const onFeeChange = useCallback(
    (e) => setFee(e.target.value), [],
  );

  const calculateSavingValue = useMemo(() => calculateSaving(propertyValue, fee, MIN_SAVING_WITHOUT_VAT), [propertyValue, fee]);

  return (
    <div
      css={styles.base}
      $compact={compact}
    >
      <div css={styles.confBox}>
        <div
          css={styles.valueBox}
          $compact={compact}
        >
          <h4
            css={styles.smallLabel}
            $compact={compact}
          >
            Prezzo del tuo immobile
          </h4>
          <div css={styles.paramValue}>{formatEuro(propertyValue)}</div>
        </div>
        <InputRange
          dataRef="input-range-property"
          value={propertyValue}
          onChange={onPropertyValueChange}
          min={PROPERTY_MIN_VALUE}
          max={PROPERTY_MAX_VALUE}
          step={PROPERTY_VALUE_UNIT}
        />
        <div
          css={styles.valueBox}
          $compact={compact}
        >
          <h4
            css={styles.smallLabel}
            $compact={compact}
          >
            Commissioni agenzia classica <span css={styles.smallerLabel}>(+IVA)</span>
          </h4>
          <div css={styles.paramValue}>{fee}%</div>
        </div>
        <InputRange
          dataRef="input-range-fee"
          value={fee}
          onChange={onFeeChange}
          min={FEE_MIN_VALUE}
          max={FEE_MAX_VALUE}
          step={FEE_VALUE_UNIT}
        />
      </div>
      <div
        css={styles.savingBox}
        $compact={compact}
      >
        <div css={styles.innerSavingBox}>
          {!compact && (
            <div css={styles.logo}>
              <Pictogram />
            </div>
          )}
          <h4
            css={styles.label}
            $compact={compact}
          >
            {priceLabel}
          </h4>
          <div
            css={styles.savingValue}
            $compact={compact}
          >
            {formatEuro(calculateSavingValue)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingCalculator;
